import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import CreatePlaylistIllustration from '~/images/illustrations/create-playlist.svg';
import type {
  PlaylistList__PlaylistFolder as Folder,
  PlaylistList__Playlist as Playlist,
} from '../../queries/queries.generated';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      illustration={CreatePlaylistIllustration}
      header={t('pages.playlists.components.empty_state.header')}
      description={t('pages.playlists.components.empty_state.description')}
    />
  );
};

export interface TableProps {
  loading: boolean;
  playlists: readonly Playlist[];
  folders: ReadonlyArray<Folder>;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
  search?: boolean;
}

type PlaylistOrFolder = Playlist | Folder;

export const Table = ({
  folders,
  loading,
  onCheck,
  playlists,
  selectedIds,
  search = false,
}: TableProps) => {
  const columns = useColumns();
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const link = useLink();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  searchParams.set('page', '1');
  const combinedData = [...folders, ...playlists];

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      kind: !isLargeAndDown,
      playlistCount: !search && !isSmallAndDown,
      playlistItems: !isSmallAndDown,
      runtime: !isLargeAndDown,
      showCount: !isLargeAndDown,
    }));
  }, [isLargeAndDown, isSmallAndDown, search]);

  return (
    <ScrollableDataGrid
      checkboxSelection={!isSmallAndDown}
      columns={columns}
      columnVisibilityModel={showColumns}
      disableRowSelectionOnClick
      getRowId={(row) => (row.__typename === 'Playlist' ? row.id : `PlaylistFolder-${row.id}`)}
      isRowSelectable={({ row }) => row.__typename !== 'PlaylistFolder'}
      loading={loading}
      onColumnVisibilityModelChange={setShowColumns}
      onRowSelectionModelChange={onCheck}
      onRowClick={({ row }: GridRowParams<PlaylistOrFolder>) => {
        if (row.__typename === 'PlaylistFolder') {
          navigate({ ...link(`/playlists/${row.id}`), search: searchParams.toString() });
          return;
        }
        navigate({
          ...link(`/playlists/${row.playlistFolderId}/playlist/${row.id}/design`),
          search: searchParams.toString(),
        });
      }}
      rows={combinedData}
      rowSelectionModel={selectedIds}
      slots={{
        baseCheckbox: ListCheckbox,
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
